import React, { useState, useEffect } from 'react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { userRoleService } from '../../../services/userRoleService';

const FilterBar = ({ filters, onFilterChange, onClearFilters, resultCount }) => {
  const [roleOptions, setRoleOptions] = useState([
    { value: 'all', label: 'Tous les rôles' }
  ]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setIsLoadingRoles(true);
      const roles = await userRoleService?.getAllRoles();

      const options = [
        { value: 'all', label: 'Tous les rôles' },
        ...roles
      
          ?.map(role => ({
            value: role?.roleType,
            label: role?.roleName,
            description: ""
          }))
      ];
      
      setRoleOptions(options);
    } catch (error) {
      console.error('Erreur lors du chargement des rôles pour les filtres:', error);
      // Fallback aux rôles par défaut en français
      setRoleOptions([
        { value: 'all', label: 'Tous les rôles' },
        { value: 'administrator', label: 'Administrateur' },
        { value: 'production_supplier', label: 'Fournisseur de Production' },
        { value: 'dentist_client', label: 'Client Dentiste' },
        { value: 'dentist_supplier', label: 'Dentiste Fournisseur' },
        { value: 'distributor', label: 'Distributeur' }
      ]);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const statusOptions = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'active', label: 'Actif' },
    { value: 'inactive', label: 'Inactif' },
  ];

  const hasActiveFilters =
    filters?.search ||
    filters?.role !== 'all' ||
    filters?.status !== 'all';

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6 shadow-sm">
      <div className="flex flex-col lg:flex-row gap-4 mb-4">
        {/* Barre de recherche */}
        <div className="flex-1">
          <div className="relative">
            <Icon 
              name="Search" 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
            />
            <Input
              type="search"
              placeholder="Rechercher par nom, email ou rôle..."
              value={filters?.search}
              onChange={(e) => onFilterChange('search', e?.target?.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Filtres */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Filtre par rôle */}
          <div className="w-full sm:w-52">
            <Select
              options={roleOptions}
              value={filters?.role}
              onChange={(value) => onFilterChange('role', value)}
              placeholder="Filtrer par rôle"
              disabled={isLoadingRoles}
            />
          </div>

          {/* Filtre par statut */}
          <div className="w-full sm:w-48">
            <Select
              options={statusOptions}
              value={filters?.status}
              onChange={(value) => onFilterChange('status', value)}
              placeholder="Filtrer par statut"
            />
          </div>

        </div>
      </div>

      {/* Barre de résultats et actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 border-t border-border">
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{resultCount}</span> utilisateur{resultCount !== 1 ? 's' : ''} trouvé{resultCount !== 1 ? 's' : ''}
          </p>
          
          {hasActiveFilters && (
            <div className="flex items-center gap-2">
              <div className="h-4 w-px bg-border"></div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {filters?.search && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium">
                    <Icon name="Search" size={12} />
                    Recherche
                  </span>
                )}
                {filters?.role !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-500/10 text-blue-600 text-xs font-medium">
                    <Icon name="Shield" size={12} />
                    {roleOptions?.find(r => r?.value === filters?.role)?.label || filters?.role}
                  </span>
                )}
                {filters?.status !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-green-500/10 text-green-600 text-xs font-medium">
                    <Icon name="CheckCircle2" size={12} />
                    {statusOptions?.find(s => s?.value === filters?.status)?.label || filters?.status}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            iconName="X"
            iconPosition="left"
            iconSize={16}
            className="hover:bg-destructive/10 hover:text-destructive"
          >
            Effacer les filtres
          </Button>
        )}
      </div>
    </div>
  );
};

export default FilterBar;